import { useEffect, useState } from "react";
import { getActiveProjectGraph, getActiveProjectId } from "../data/sampleGraph";
import type { ProjectGraph } from "../types/ontology";

/* useActiveProjectGraph — single source of truth for cockpit panels.
 *
 *   • Re-reads on `foyrmind:projectChanged` so every surface flips
 *     in lockstep when the designer switches projects.
 *   • Exposes `isLinkedPending` so panels that depend on a typed
 *     graph (rules, scenarios, activity feed) can render a clean
 *     "bundle pending" empty state for Foyr-linked projects whose
 *     bundle hasn't ingested yet — instead of showing Riverside
 *     data re-stamped with the user's project name. */

export interface ActiveProject {
  graph: ProjectGraph;
  activeId: string;
  isLinkedPending: boolean;
}

function hasIngestedBundle(activeId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem("foyrmind.projectGraphs.v1");
    if (!raw) return false;
    const map = JSON.parse(raw) as Record<string, { graph?: { nodes?: unknown[] } }>;
    const entry = map?.[activeId];
    // A real ingested graph has ≥ 2 nodes (Project + at least one
    // typed entity from the Foyr bundle). The 1-node placeholder
    // we ship for linked-pending doesn't count.
    return !!(entry?.graph?.nodes && Array.isArray(entry.graph.nodes) && entry.graph.nodes.length >= 2);
  } catch { return false; }
}

function read(): ActiveProject {
  const activeId = getActiveProjectId();
  const isLinked = activeId.startsWith("foyr-");
  return {
    graph: getActiveProjectGraph(),
    activeId,
    // Linked Foyr project counts as "pending" UNTIL the extension
    // forwards a real bundle and the Onboarding Agent persists a
    // typed graph with real entities. After ingest, the cockpit
    // shows the real graph + every panel lights up.
    isLinkedPending: isLinked && !hasIngestedBundle(activeId),
  };
}

export function useActiveProjectGraph(): ActiveProject {
  const [state, setState] = useState<ActiveProject>(() => read());
  useEffect(() => {
    function onChange(): void { setState(read()); }
    function onStorage(e: StorageEvent): void {
      if (!e.key) return;
      if (e.key.includes("activeProjectId") || e.key.includes("userProjects") || e.key.includes("projectGraphs") || e.key.includes("foyrBundles")) onChange();
    }
    window.addEventListener("foyrmind:projectChanged", onChange as EventListener);
    window.addEventListener("foyrmind:foyrBundleArrived", onChange as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("foyrmind:projectChanged", onChange as EventListener);
      window.removeEventListener("foyrmind:foyrBundleArrived", onChange as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return state;
}
